import {
  Lit3formBlock,
  html,
  unsafeHTML,
  classMap,
} from '@Root/lib/lit-3form-block';

import { styles } from './css';

import {
  isFeatureDisabled,
  useSampleLimits,
  useAddToCartModal,
  useCartConfirmationModal,
  waitForElement,
  logErrorToSentry,
} from '@Helpers/helpers.js';
import { imagebossUrl } from '@Helpers/imageboss';

import {
  fetchCart,
  checkProductAvailability,
  addToCart,
} from '@Root/spree/api';
import {
  updateCartIndicator,
  changeProductNameInModal,
  changeProductSizeInfoInModal,
  changeProductImageInModal,
} from '@Root/spree/dom';

import find from 'lodash.find';

import '../walter/barrel';
import '../walter/visualize-products-barrel';
import '../walter/dynamic-downloads-barrel';

const imageSelectHashPrefix = '#w-image-';
const imageSelectHashRegExp = new RegExp(`${imageSelectHashPrefix}(\\d+)`);

export class Jesse extends Lit3formBlock {
  static get characterName() {
    return 'jesse';
  }

  static get characterStyles() {
    return [...styles];
  }

  static properties = {
    _carouselImagesArray: { type: Array },
    _selectedCarouselImageId: {
      type: Number,
      state: true,
      hasChanged: (newValue, oldValue) => {
        const hasChanged = newValue !== oldValue;

        if (hasChanged && window.location.hash.match(imageSelectHashRegExp))
          window.location.hash = `${imageSelectHashPrefix}${newValue + 1}`;

        return hasChanged;
      },
    },
    _carouselHeight: { type: Number },
    _carouselBtnsTopValue: { type: Number },
    _barrels: { type: Array },
    cart: { type: Object },
    includedPropertiesById: { type: Object },
    secondaryCtaHidden: { type: Boolean },
    secondaryCtaDisabled: { type: Boolean },
    _loadedThreekitImages: { type: Object },
    _selectedCustomPattern: { type: Object },
    _selectedHardwareFinish: { type: Object },
    _productProperties: { type: Object },
    _spreeProduct: { type: Object },
    _downloadsArray: { type: Object },
    _showMobileStickyHeader: { type: Boolean },
  };

  constructor() {
    super();

    this._carouselImagesArray = this.content.images || [];

    const matches = window.location.hash.match(imageSelectHashRegExp),
      imageIndex = matches && matches[1] - 1;

    if (
      typeof imageIndex === 'number' &&
      imageIndex <= this._carouselImagesArray.length
    )
      this._selectedCarouselImageId = imageIndex;
    else this._selectedCarouselImageId = 0;

    if (
      typeof imageIndex === 'number' &&
      imageIndex <= this._carouselImagesArray.length
    )
      this._selectedCarouselImageId = imageIndex;
    else this._selectedCarouselImageId = 0;

    this._barrels = this.barrels = this.content.barrels || [];

    this._spreeProduct = this.sampleDataFromWalter;

    this.secondaryCtaHidden = false;
    this.secondaryCtaDisabled = true;
    this._showMobileStickyHeader = false;

    this.addEventListener('visualizedProductSelected', e => {
      this._selectedCustomProduct = e.detail?.option || {};
      this.updateCarouselImages();
      this.fetchDownloadsBarrelContent();
    });

    this.addEventListener('hardwareColorSelected', e => {
      this._selectedHardwareFinish = e.detail.option || {};
      this.updateCarouselImages();
    });

    this._downloadsArray = [];
    this._loadedThreekitImages = {};
  }

  async connectedCallback() {
    super.connectedCallback();

    try {
      await this.setupSpreeData();
    } catch (error) {
      logErrorToSentry(error);
      this.secondaryCtaHidden = true;
    }

    window.addEventListener('scroll', this._handleScroll.bind(this), false);
    document.addEventListener(
      'layout-shift-required',
      this._handleScroll.bind(this),
      false
    );
    // Initial trigger in case the page loads in a scrolled position
    this._handleScroll();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._handleScroll.bind(this), false);
    document.addEventListener(
      'layout-shift-required',
      this._handleScroll.bind(this),
      false
    );
    super.disconnectedCallback();
  }

  async _handleScroll() {
    if (!window.useMobileLayout) {
      this._showMobileStickyHeader = false;
      return;
    }
    const jesseBlockEl = await waitForElement('jesse-block');
    if (!!jesseBlockEl) {
      const stickyWrapperEl = jesseBlockEl.shadowRoot.getElementById(
          'sticky-hero-wrapper-mobile'
        ),
        stickyWrapperHeight = stickyWrapperEl
          ? stickyWrapperEl.getBoundingClientRect().height
          : 0,
        headerBottomPosition = jesseBlockEl.shadowRoot
          .getElementById('hero-img')
          .getBoundingClientRect().bottom;
      this._showMobileStickyHeader =
        headerBottomPosition <= stickyWrapperHeight;

      const jesseBox = jesseBlockEl.getBoundingClientRect(),
        viewableJesse = jesseBox?.height + jesseBox?.y || 0;
      if (viewableJesse <= stickyWrapperHeight) {
        stickyWrapperEl.style['top'] = `${-Math.abs(
          stickyWrapperHeight - viewableJesse
        )}px`;
      } else {
        stickyWrapperEl.style['top'] = '0px';
      }
    }
  }

  render() {
    return html``;
    return html`
      <div class="block-wrapper">
        <div class="block-grid jesse-layout">
          <h2 class="block-title">${unsafeHTML(this.headerStr)}</h2>
          <p class="description">
            ${unsafeHTML(this.descriptionStr)}
          </p>
          <div
            class=${classMap({
              'carousel-images': true,
              'print-none': true,
              hidden: this.hideImageSelections,
            })}
          >
            <div class="" style="height: ${this._carouselHeight}px">
              ${this._carouselImagesArray.map((image, index) =>
                this.carouselImageHTML(image, index)
              )}
            </div>
          </div>
          <div class="carousel-hero" id="carousel-hero">
            ${this.mobileStickyHeaderHTML}
            <div class="carousel-hero-wrapper" id="hero-wrapper">
              <img
                id="hero-img"
                class="carousel-img"
                src=${this.heroImgUrl}
                decoding="async"
                @load=${this.carouselImageLoaded}
              />
              <div
                class=${classMap({
                  'carousel-arrows': true,
                  'print-none': true,
                  hidden: this.hideImageSelections,
                })}
              >
                <span
                  @click="${() => this.changeCarouselImage('previous')}"
                  class="left-arrow"
                >
                  <img src="/static/icons/SVG/arrow-black-5x10.svg" />
                </span>
                <span
                  @click="${() => this.changeCarouselImage('next')}"
                  class="right-arrow"
                >
                  <img src="/static/icons/SVG/arrow-black-5x10.svg" />
                </span>
              </div>
            </div>
            <div class="carousel-bottom-row" id="hero-caption">
              <p class="font-size-small image-caption">
                ${unsafeHTML(this.thumbnailDescription)}
              </p>
              <a
                href=${this.selectedCarouselImage.full_resolution_src}
                target="_blank"
                class="print-none"
              >
                <span id="download-icon">
                  <p>Download Image</p>
                  <img src="/static/icons/SVG/download-12x16.svg" />
                </span>
              </a>
            </div>
          </div>
          <cta-button
            cta-data=${this.ctaData}
            cta-type="primary"
            @click="${this.requestMoreInfoClicked}"
            class="primary-cta"
          ></cta-button>
          <cta-button
            cta-data=${this.secondaryCtaData}
            cta-type="secondary"
            @click="${this.secondaryCtaClickHandler}"
            .hidden="${this.secondaryCtaHidden}"
            .disabled="${this.secondaryCtaDisabled}"
            class="secondary-cta"
          ></cta-button>
          ${this._barrels.length > 0
            ? html`
                <div class="barrels">
                  ${this._barrels.map(this.renderBarrel.bind(this))}
                </div>
              `
            : html``}
          <div class="stupid-spacer"></div>
          ${this.horizontalRule}
        </div>
      </div>
    `;
  }

  carouselImageLoaded() {
    // Only set these once to keep the buttons in the same location
    if (this._carouselHeight > 0 && this._carouselBtnsTopValue > 0) return;

    // Ensure that the image is rendered before setting the button height
    if (!this.heroImageElement || this.heroImageElement.offsetHeight === 0)
      return;

    if (!!this.heroImageElementWrapper && !!this.heroImageElement) {
      // TODO break points should be JS variables and moved from CSS variables
      const offsetDivider = window.innerWidth > 600 ? 2 : 1;
      this._carouselBtnsTopValue =
        this.heroImageElement.offsetHeight / offsetDivider;
      this._carouselHeight = this.heroImageElementWrapper.offsetHeight;
    }
  }

  get mobileStickyHeaderHTML() {
    return html`
      <div
        id="sticky-hero-wrapper-mobile"
        class=${classMap({
          'hide-sticky-wrapper': !this._showMobileStickyHeader,
        })}
      >
        <div
          class="carousel-hero-content-wrapper"
          id="hero-wrapper-mobile-jesse"
        >
          <img
            id="hero-img-mobile"
            class="carousel-img-sticky"
            src=${this.heroImgUrl}
            decoding="async"
            @load=${this.carouselImageLoaded}
          />
          <div
            class=${classMap({
              'carousel-arrows': true,
              'carousel-arrows-sticky': true,
              'print-none': true,
              hidden: this.hideImageSelections,
            })}
          >
            <span
              @click="${() => this.changeCarouselImage('previous')}"
              class="left-arrow"
            >
              <img src="/static/icons/SVG/arrow-black-5x10.svg" />
            </span>
            <span
              @click="${() => this.changeCarouselImage('next')}"
              class="right-arrow"
            >
              <img src="/static/icons/SVG/arrow-black-5x10.svg" />
            </span>
          </div>
        </div>
        <div class="sticky-header-content">
          <h1>${unsafeHTML(this.headerStr)}</h1>
          <p class="font-size-small image-caption">
            ${unsafeHTML(this.thumbnailDescription)}
          </p>
        </div>
      </div>
    `;
  }

  carouselImageHTML(image, index) {
    if (!!image[1].src) {
      const dimensions = { width: null, height: '100' };
      return html`
        <div class="image">
          <img
            src=${imagebossUrl(image[1].src, dimensions)}
            data-index=${index}
            @click="${this._updateSelectedCarouselImage}"
            class="${this.isCurrentCarouselImage(image)
              ? 'selected-image'
              : 'not-selected-image'}"
          />
        </div>
      `;
    } else {
      return html``;
    }
  }

  get heroImageElement() {
    return this.shadowRoot.getElementById('hero-img');
  }

  get heroImageElementWrapper() {
    return this.shadowRoot.getElementById('hero-wrapper');
  }

  isCurrentCarouselImage(image) {
    return image[1].src === this.selectedCarouselImage.src;
  }

  barrelHeaderIcon(isExpanded) {
    return isExpanded
      ? '/static/icons/SVG/minimize-dash.svg'
      : '/static/icons/SVG/expand-plus.svg';
  }

  get selectedCarouselImage() {
    return this.carouselImageIsSet
      ? this._carouselImagesArray[this._selectedCarouselImageId][1]
      : '';
  }

  _updateSelectedCarouselImage(event) {
    const index = event.target.getAttribute('data-index');
    this._selectedCarouselImageId = parseInt(index);
  }

  openContactRep() {
    MicroModal.show('modal-contact-rep');
  }

  renderBarrel(barrel) {
    const isVisualizationBarrel = barrel.threekit_stages_path;
    const isDynamicDownloads = barrel.downloads;

    if (
      isFeatureDisabled('materialDetailsPdfLink') &&
      barrel.downloads &&
      barrel.downloads.length > 4
    )
      barrel.downloads.pop();

    if (isVisualizationBarrel) {
      barrel.headerStr = this.headerStr;
      return html`
        <visualize-products-barrel
          content=${JSON.stringify(barrel)}
          ?expanded=${barrel.expanded}
          id="visualize"
        ></visualize-products-barrel>
      `;
    } else if (isDynamicDownloads) {
      return html`
        <dynamic-downloads-barrel
          content=${JSON.stringify(barrel)}
          ?expanded=${barrel.expanded}
        ></dynamic-downloads-barrel>
      `;
    } else if (!isVisualizationBarrel && !isDynamicDownloads) {
      return html`
        <walter-barrel
          content=${JSON.stringify(barrel)}
          ?expanded=${barrel.expanded}
        ></walter-barrel>
      `;
    }
  }

  get titleFromWalter() {
    return !!document.getElementsByTagName('walter-block')[0]
      ? JSON.parse(
          document
            .getElementsByTagName('walter-block')[0]
            .getAttribute('content')
        ).header
      : '';
  }

  get sampleDataFromWalter() {
    const walterContent = JSON.parse(
      document.getElementsByTagName('walter-block')[0].getAttribute('content')
    );
    return walterContent?.sample_types['varia'] || {};
  }

  get blockLabel() {
    return this.content.label;
  }

  get selectedSampleTypeKey() {
    return this._availableSampleTypes && this.availableSampleTypes[0];
  }

  get selectedSampleType() {
    return (
      this.selectedSampleTypeKey &&
      this.content.sample_types[this.selectedSampleTypeKey]
    );
  }

  get selectedSampleTypeDisplayValue() {
    return this.selectedSampleType && this.selectedSampleType.sample_type_text;
  }

  get addToCartQuantity() {
    return 1;
  }

  get carouselImageIsSet() {
    return (
      this._carouselImagesArray[this._selectedCarouselImageId] &&
      this._carouselImagesArray[this._selectedCarouselImageId][1]
    );
  }

  get headerStr() {
    let header = this.content.header || '';
    this.content.subheader
      ? (header = header + ' ' + this.content.subheader)
      : '';
    return this.withLineBreaks(header);
  }

  get descriptionStr() {
    let descriptionStr = this.content.description || '';
    descriptionStr = descriptionStr.replace(
      '{{pageTitle}}',
      this.titleFromWalter
    );
    return this.withLineBreaks(descriptionStr);
  }

  get returnUrl() {
    return this.content.return_url;
  }

  get thumbnailDescription() {
    return (
      this.withLineBreaks(this.selectedCarouselImage.thumbnail_description) ||
      ''
    );
  }

  get ctaData() {
    if (this.content.cta) {
      this.content.cta.twoColumns = true;
      this.content.cta.fullWidth = true;
    }

    return JSON.stringify(this.content.cta);
  }

  get secondaryCtaData() {
    return JSON.stringify({
      url: '',
      text: 'Order 4"x4" Sample',
      fullWidth: true,
      twoColumns: true,
      isSampleOrderCta: true,
    });
  }

  get fallbackCtaData() {
    return JSON.stringify(this.content.fallback_cta);
  }

  get fallbackCtaHidden() {
    return !this.secondaryCtaHidden;
  }

  get fallbackCtaDisabled() {
    return this.fallbackCtaHidden;
  }

  set barrels(barrels) {
    let formattedBarrels = barrels || [];
    formattedBarrels.push({ downloads: true });
    formattedBarrels.forEach((b, index) => {
      b.expanded = true;
      b.order = index;
    });
    return formattedBarrels;
  }

  get hideImageSelections() {
    return this._carouselImagesArray.length <= 1;
  }

  get heroImgUrl() {
    return this.imagebossUrl(this.selectedCarouselImage.src, null, 700);
  }

  fetchThreekitRenderedImages() {
    const searchParams = new URLSearchParams({
      threekit_hardware_finish_id: this._selectedHardwareFinish.id,
      threekit_stage_id: this._selectedCustomProduct.id,
      render_interlayer_id: this._selectedCustomProduct.render_interlayer_id,
    });
    const layersUrl = `/threekit_layers?${searchParams}`;

    if (this._loadedThreekitImages[layersUrl]) {
      return this.addThreekitImages(this._loadedThreekitImages[layersUrl]);
    }

    const init = {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    };

    fetch(layersUrl, init)
      .then(response => {
        if (response.ok) {
          return response.json();
        }
      })
      .then(json => {
        if (!!json) {
          this._loadedThreekitImages[layersUrl] = json.threekit_layers;
          this.addThreekitImages(this._loadedThreekitImages[layersUrl]);
        }
      })
      .catch(error => {
        logErrorToSentry(error);
      });
  }

  addThreekitImages(threekitLayers = []) {
    let { _carouselImagesArray } = this;

    for (let i = 0; i < _carouselImagesArray.length; i++) {
      if (_carouselImagesArray[i][1].rendered) {
        _carouselImagesArray.splice(i, 1);
        i--;
      }
    }

    threekitLayers.forEach((layer, index) => {
      if (layer.camera_id === 1) {
        this._camera1ImageUrl = layer.carousel_src;
      }
      const formattedLayer = [];
      // display order will be sent by the server at some point
      layer.display_order = _carouselImagesArray.length + index;
      layer.rendered = true;
      layer.thumbnail_description = this.renderedImageThumbnailDescription();
      formattedLayer.push(layer.display_order.toString());
      formattedLayer.push(layer);
      this._carouselImagesArray.push(formattedLayer);
    });

    if (this.content.full_panel_image_url) {
      const fullPanelImage = [
        '',
        {
          src: this.content.full_panel_image_url,
          thumbnail_description: this.content?.full_panel_image_description
            ?.thumbnail_description,
          rendered: true,
        },
      ];
      this._carouselImagesArray.push(fullPanelImage);
    }

    // If there are fewer incoming images than the currently selected image,
    // set the selected image to the first one
    // Or, if the user was on the full panel image, take them back to the full
    // scene when they select a new one.
    if (
      this._selectedCarouselImageId > _carouselImagesArray.length - 1 ||
      this._selectedCarouselImageId === 2
    ) {
      this._selectedCarouselImageId = 0;
    }

    this.requestUpdate();
  }

  changeCarouselImage(direction) {
    const imagesCount = this._carouselImagesArray.length;
    const currentId = this._selectedCarouselImageId;
    if (direction === 'previous') {
      currentId === 0
        ? (this._selectedCarouselImageId = imagesCount - 1)
        : this._selectedCarouselImageId--;
    } else if (direction === 'next') {
      currentId === imagesCount - 1
        ? (this._selectedCarouselImageId = 0)
        : this._selectedCarouselImageId++;
    }
  }

  updateCarouselImages() {
    if (!this.hasSelectedCustomProduct || !this.hasSelectedHardwareColor) {
      return;
    }

    this.fetchThreekitRenderedImages();
  }

  fetchDownloadsBarrelContent() {
    if (this._selectedCustomProduct) {
      const stageId = this._selectedCustomProduct?.id;
      const downloadsUrl = this.content.render_stage_download_links_endpoint.replace(
        ':stage_id',
        `${stageId}`
      );

      const init = {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      };

      fetch(downloadsUrl, init)
        .then(response => {
          if (response.ok) {
            return response.json();
          }
        })
        .then(json => {
          this.updateDownloadsBarrelContent(json);
        })
        .catch(error => {
          logErrorToSentry(error);
        });
    }
  }

  updateDownloadsBarrelContent(downloadsArr) {
    this._downloadsArray = downloadsArr;
    const updateDownloadsArrayEvent = new CustomEvent('updateDownloadsArray', {
      detail: {
        downloadsArr,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(updateDownloadsArrayEvent);
  }

  requestMoreInfoClicked() {
    const imageUrl = this._camera1ImageUrl
      ? this._camera1ImageUrl
      : this.imagebossUrl(this._carouselImagesArray[0][0].src, null, 700);

    const textDescription = this.hasSelectedCustomProduct
      ? this.thumbnailDescription
      : `${this.blockLabel} - ${this.headerStr}`;

    const sessionCookieData = {
      description: textDescription,
      image: {
        url: imageUrl,
        description: this.thumbnailDescription,
      },
    };

    window.sessionStorage.setItem(
      'requestInfoData',
      JSON.stringify(sessionCookieData)
    );
  }

  renderedImageThumbnailDescription() {
    const material = `Material | Varia`;
    const pattern = this.titleFromWalter
      ? `\nPattern | ${this.titleFromWalter}`
      : '';
    const hardwareSystem = this.headerStr
      ? `\nHardware System | ${this._selectedCustomProduct.name}`
      : '';
    const hardwareFinish = this._selectedHardwareFinish.display_name
      ? `\nHardware Finish | ${this._selectedHardwareFinish.display_name}`
      : '';
    return `${material}${pattern}${hardwareSystem}${hardwareFinish}`;
  }

  get configuredPatternName() {
    const pattern = this._selectedCustomProduct;
    return pattern.parentName
      ? `${pattern.parentName} ${pattern.name}`
      : `${pattern.name}`;
  }

  get hasSelectedCustomProduct() {
    return (
      !!this._selectedCustomProduct &&
      typeof this._selectedCustomProduct === 'object' &&
      Object.keys(this._selectedCustomProduct).length > 0
    );
  }

  get hasSelectedHardwareColor() {
    return this._selectedHardwareFinish
      ? Object.keys(this._selectedHardwareFinish).length > 0
      : false;
  }

  async setupSpreeData() {
    if (!this.spreeProductId) return;

    this.cart = await fetchCart();
    if (this.cart) updateCartIndicator(this.cart.data.attributes.item_count);

    this.includedPropertiesById = {};
    const productAvailabilityResponse = await checkProductAvailability(
      this.spreeProductId
    );

    this._productIsAvailable = !productAvailabilityResponse.isFail();
    if (this._productIsAvailable) {
      this._productProperties = productAvailabilityResponse.success().included;
      this.secondaryCtaDisabled = false;
    } else {
      this.secondaryCtaDisabled = true;
    }
  }

  get secondaryCtaClickHandler() {
    let clickHandler;
    if (
      JSON.parse(this.secondaryCtaData).isSampleOrderCta &&
      useSampleLimits()
    ) {
      clickHandler = this.openSampleOrderModal;
    } else {
      clickHandler = this.addSelectedSampleToCart;
    }
    return clickHandler;
  }

  openSampleOrderModal() {
    useAddToCartModal();
    const setupAddToCartDataEvent = new CustomEvent('setupAddToCartData', {
      detail: {
        productVariantId: this.spreeProductVariantId,
        headerStr: this.spreeProductTitle,
        imageUrl: this.spreeSampleImageUrl.replace(':dimensions', 'width/258'),
        productSize: `4"x4"`,
        sampleType: this.spreeProductSlug,
      },
      bubbles: true,
      composed: true,
    });
    const el = document.getElementById('modal-request-sample');
    el.dispatchEvent(setupAddToCartDataEvent);
    this.showMicroModal();
  }

  showMicroModal() {
    MicroModal.show('modal-request-sample');
  }

  get selectedSampleTypeImage() {
    return (
      !!this.selectedSampleType &&
      !!this.selectedSampleType.image_src &&
      this.selectedSampleType.image_src.replace(':dimensions', 'width/258')
    );
  }

  async addSelectedSampleToCart() {
    if (!this._productIsAvailable || this.secondaryCtaDisabled) return;
    useCartConfirmationModal();

    this.cart = await addToCart(
      this.spreeProductId,
      this.addToCartQuantity,
      this.spreeProductSlug
    );

    if (this.cart) {
      updateCartIndicator(this.cart.item_count);

      const productSize = find(this._productProperties, {
        attributes: { name: 'size' },
      });

      changeProductSizeInfoInModal(
        productSize.attributes.value,
        this.sampleTypeText
      );
      changeProductNameInModal(this.spreeProductTitle);
      changeProductImageInModal(this.spreeProductImage);
    }
  }

  get spreeProductId() {
    return this.content.spree_sample_product?.product_id;
  }

  get spreeProductVariantId() {
    return this.content.spree_sample_product?.variant_id;
  }

  get spreeProductSlug() {
    return this.content.spree_sample_product?.slug || '';
  }

  get spreeSampleImageUrl() {
    return this.content.spree_sample_product?.image_src || '';
  }

  get sampleTypeText() {
    return this._spreeProduct?.sample_type_text;
  }

  get spreeProductTitle() {
    return this.titleFromWalter;
  }

  get spreeProductImage() {
    return this.imagebossUrl(this._spreeProduct.image_src, null, 300);
  }
}
Jesse.defineElement();
